import type { FormGroup, UntypedFormArray } from '@angular/forms';
import type { Observable } from 'rxjs';
import type { AfterViewInit, OnInit } from '@angular/core';
import type { TestimonialWidgetData } from '@web/widgets/testimonial/type';
import type { EduCourse } from '@common/types/edu-course.type';
import {
    type TestimonialButtonSettings,
    type TestimonialButtonSettingsFormType,
    type TestimonialSettingsFormType,
    type TestimonialWidgetDataFormType,
} from '@web/widgets/testimonial/type/testimonial-widget.type';

import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { ETestimonialCount, ETestimonialLayout } from '@common/enums';
import { TestimonialsCollectionFacade } from '@web/store/collections/testimonials/testimonials.facade';
import { EduCoursesCollectionFacade } from '@web/store/collections/edu-courses/edu-courses.facade';

import { testimonialWidgetDataFormGroup } from '@web/widgets/testimonial/testimonial/testimonial-widget.testimonial';
import { WebBaseControlComponent } from '@web/widgets/common/abstract/web-base-control.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { TestimonialSettingsComponent } from './testimonial-settings/testimonial-settings.component';
import { TestimonialButtonSettingsComponent } from './testimonial-button-settings/testimonial-button-settings.component';

enum CurrentSettings {
    MAIN = 'main',
    BUTTON = 'button',
    SETTINGS = 'settings',
}

@Component({
    selector: 'web-testimonial-control',
    templateUrl: './testimonial-control.component.html',
    styleUrls: ['./testimonial-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, TestimonialSettingsComponent, TestimonialButtonSettingsComponent],
})
export class TestimonialControlComponent
    extends WebBaseControlComponent<TestimonialWidgetData>
    implements OnInit, AfterViewInit
{
    private readonly eduCourseFacade: EduCoursesCollectionFacade = inject(EduCoursesCollectionFacade);
    private readonly testimonialsCollectionFacade: TestimonialsCollectionFacade = inject(TestimonialsCollectionFacade);

    public readonly eduCourses$: Observable<EduCourse[]> = this.eduCourseFacade.eduCourses$;
    public readonly courses$: Observable<EduCourse[]> = this.appFacade.isAdminCollectionWrapper$(this.eduCourses$);

    public readonly testimonialLayoutOptions = [
        { label: 'testimonial_carousel', type: ETestimonialLayout.carousel },
        { label: 'testimonial_grid', type: ETestimonialLayout.grid },
    ];

    public readonly testimonialCountOptions = [
        { label: 'testimonial_count_type_all', type: ETestimonialCount.all },
        { label: 'testimonial_count_type_last20', type: ETestimonialCount.last20 },
        { label: 'testimonial_count_type_last50', type: ETestimonialCount.last50 },
        { label: 'testimonial_count_type_last100', type: ETestimonialCount.last100 },
        { label: 'testimonial_count_type_last200', type: ETestimonialCount.last200 },
    ];

    public testimonialSettings: FormGroup<TestimonialSettingsFormType> = null;
    public testimonialButtonSettings: TestimonialButtonSettings = null;
    public currentSettings: CurrentSettings = CurrentSettings.MAIN;
    public settingEnum = CurrentSettings;

    public ngAfterViewInit(): void {
        if (this.selectedCourseId.value) this.setTestimonialCourse(null, this.selectedCourseId.value);
    }

    protected initForm(): void {
        this.controlForm = this.fb.group({
            data: testimonialWidgetDataFormGroup(),
        });
    }

    protected setFormData(): void {
        this.controlForm.patchValue({ data: { ...this.widgetData } }, { emitEvent: false });

        const widgetItems = this.widgetData?.items || [];
        this.items.clear({ emitEvent: false });
        widgetItems.forEach((el) => this.items.push(this.fb.control(el), { emitEvent: false }));
        this.items.markAsPristine();
    }

    public setTestimonialLayout($event: Event): void {
        const target = $event.target as HTMLSelectElement;
        const layout = target.value as ETestimonialLayout;

        this.layout.patchValue(layout);
    }

    public setTestimonialCourse($event: Event, courseId?: number) {
        let selectedCourseId;

        if ($event) {
            const target = $event.target as HTMLSelectElement;
            selectedCourseId = +target.value;
            this.selectedCourseId.patchValue(selectedCourseId);
        } else {
            selectedCourseId = +courseId;
        }

        this.testimonialsCollectionFacade.testimonials$(selectedCourseId).subscribe((items) => {
            this.items.clear({ emitEvent: false });
            items.forEach((el) => this.items.push(this.fb.control(el), { emitEvent: false }));
            this.items.updateValueAndValidity();
            this.items.markAsPristine();
            this.changeDetectorRef.markForCheck();
        });
    }

    public setTestimonialCount($event: Event) {
        const target = $event.target as HTMLSelectElement;
        const count = target.value as ETestimonialCount;

        this.testimonialCount.patchValue(count);
    }

    public setCurrentSettingsType(val: CurrentSettings): void {
        this.currentSettings = val;
    }

    public get items(): UntypedFormArray {
        return this.data.get('items') as UntypedFormArray;
    }

    public get settings(): TestimonialWidgetDataFormType['settings'] {
        return this.data.get('settings') as TestimonialWidgetDataFormType['settings'];
    }

    public get layout(): TestimonialWidgetDataFormType['layout'] {
        return this.data.get('layout') as TestimonialWidgetDataFormType['layout'];
    }

    public get buttonText(): TestimonialButtonSettingsFormType['label'] {
        return this.data.get('buttonSettings.label') as TestimonialButtonSettingsFormType['label'];
    }

    public get selectedCourseId(): TestimonialWidgetDataFormType['selectedCourseId'] {
        return this.data.get('selectedCourseId') as TestimonialWidgetDataFormType['selectedCourseId'];
    }

    public get testimonialCount(): TestimonialWidgetDataFormType['testimonialCount'] {
        return this.data.get('testimonialCount') as TestimonialWidgetDataFormType['testimonialCount'];
    }

    public get buttonSettings(): TestimonialWidgetDataFormType['buttonSettings'] {
        return this.data.get('buttonSettings') as TestimonialWidgetDataFormType['buttonSettings'];
    }
}
